import PropTypes from 'prop-types'
import { useEffect, useState, useMemo } from 'react'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { copiarCampoGrid, eliminarFilaGrid, resetModal, setModal } from 'slices/modalSlice'
import {
  ENVIO_A_CARGO_COMPRADOR,
  ENVIO_GRATIS,
  MERCADO_PAGO,
  RETIRA_COMPRADOR
} from '../../constants/mediosEnviosPagos.js'
import { validarRangoFechas } from '../../helpers/dateValidation.js'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import { Checkbox } from '../reutilizables/checkbox/Checkbox'
import GenericSelect from '../reutilizables/genericSelect/GenericSelect'
import { GenericModal } from '../reutilizables/modal/GenericModal'
import * as listaEnviosService from '../servicios/listaEnvioService'
import * as publicacionesService from '../servicios/publicacionesService'
import styles from './VentasEspeciales.module.css'

const GARANTIA_4 = 4

const camposDefault = {
  isFullWidth: false,
  isEditable: true,
  isGrid: true,
  copy: false
}

const campos = [
  {
    type: 'text',
    name: 'nombreLista',
    placeholder: 'Ingrese el nombre la lista',
    label: 'Nombre de la lista',
    isFullWidth: true,
    isEditable: false,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaDesde',
    label: 'Comienzo de la vigencia',
    isFullWidth: false,
    isEditable: false,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaHasta',
    label: 'Fin de la vigencia',
    isFullWidth: false,
    isEditable: true,
    isGrid: false
  },
  {
    type: 'text',
    name: 'zona',
    placeholder: 'Zona del envio',
    label: 'Zona',
    ...camposDefault
  },
  {
    type: 'number',
    name: 'cantidadDesde',
    label: 'Cantidad desde',
    ...camposDefault
  },
  {
    type: 'number',
    name: 'cantidadHasta',
    label: 'Cantidad hasta',
    ...camposDefault
  },
  {
    type: 'number',
    name: 'costo',
    label: 'Costo',
    ...camposDefault
  },
  {
    type: 'number',
    name: 'tEstimado',
    label: 'Dias entrega',
    ...camposDefault
  },
  {
    type: 'select',
    name: 'moneda',
    placeholder: 'Seleccione una',
    label: 'Moneda',
    options: [
      { value: 'ARS', label: 'ARS ($)', key: 1 },
      { value: 'USD', label: 'USD (U$S)', key: 2 },
      { value: 'EUR', label: 'EUR (€)', key: 3 }
    ],
    ...camposDefault,
    copy: true
  }
]

const VentasEspeciales = ({
  values,
  onChange,
  onAddGarantia,
  vieneDeAdministrar,
  idPublicacion
}) => {
  const { loginDataContext: { idUsuario } = {} } = useAuth()
  const [mediosPagos, setMediosPago] = useState([])
  const [mediosEnvios, setMediosEnvio] = useState([])
  const [garantias, setGarantias] = useState([])
  const [enviosUsuario, setEnviosUsuario] = useState([])
  const [medioEnvio, setMedioEnvio] = useState({ idLista: '', nombreLista: '' })
  const [actualizar, setActualizar] = useState(false)

  const dispatch = useDispatch()
  const { values: modalValues, errores } = useSelector((state) => state.genericModal)

  useEffect(() => {
    if (!vieneDeAdministrar) {
      if (onChange) {
        if (values.descZona !== undefined) {
          onChange({ currentTarget: { name: 'descZona', value: values.descZona } })
        }
        if (values.cantDiasEnvioGratis !== undefined) {
          onChange({
            currentTarget: {
              name: 'cantDiasEnvioGratis',
              value: values.cantDiasEnvioGratis
            }
          })
        }
        if (values.cantDiasRetiroComprador !== undefined) {
          onChange({
            currentTarget: {
              name: 'cantDiasRetiroComprador',
              value: values.cantDiasRetiroComprador
            }
          })
        }
        if (values.descZonaRetiro !== undefined) {
          onChange({
            currentTarget: { name: 'descZonaRetiro', value: values.descZonaRetiro }
          })
        }
      }
    }
  }, [
    vieneDeAdministrar,
    values.descZona,
    values.cantDiasEnvioGratis,
    values.cantDiasRetiroComprador,
    values.descZonaRetiro
  ])

  const abrirModalLista = () => {
    dispatch(
      setModal({
        size: 'lg',
        title: 'Crear lista',
        type: 'form',
        body: '',
        show: true,
        editMode: false,
        campos,
        modalId: 'crear-lista-envio'
      })
    )
  }

  useEffect(() => {
    const obtenerDatos = async () => {
      const mediosPagoRtn = await publicacionesService.buscarMedioPago()
      setMediosPago(mediosPagoRtn.data)
      const mediosEnvioRtn = await publicacionesService.buscarMedioEnvio()
      setMediosEnvio(mediosEnvioRtn.data)
      const garantiasRtn = await publicacionesService.buscarGarantia()
      setGarantias(garantiasRtn.data.filter((garantia) => garantia.idGarantia !== GARANTIA_4))
    }

    obtenerDatos()
  }, [values])

  const traerInfo = async () => {
    if (!idUsuario) return
    try {
      const idVendedor = idUsuario

      const { data } = await listaEnviosService.buscarListaPorVendedor(idVendedor)

      const unificadas = Object.values(
        data.reduce((acc, item) => {
          const key = item.nombreLista.trim().toLowerCase()
          if (!acc[key]) {
            acc[key] = {
              idLista: item.idLista,
              nombreLista: item.nombreLista,
              vigenciaDesde: item.vigenciaDesde,
              vigenciaHasta: item.vigenciaHasta,
              idVendedor: item.idVendedor,
              grilla: []
            }
          }

          if (Array.isArray(item.grilla)) {
            acc[key].grilla.push(...item.grilla)
          }

          return acc
        }, {})
      )
      setEnviosUsuario(unificadas)

      if (vieneDeAdministrar && idPublicacion) {
        const { data: listaPublicacion } = await listaEnviosService.buscarListaPorVendedor(
          idVendedor,
          idPublicacion
        )
        if (listaPublicacion && listaPublicacion.length > 0) {
          const listaAsociada = listaPublicacion[0]
          setMedioEnvio({
            idLista: String(listaAsociada.idLista),
            nombreLista: listaAsociada.nombreLista
          })
          if (onChange) {
            onChange({
              currentTarget: { name: 'idListaEnvio', value: Number(listaAsociada.idLista) }
            })
            onChange({
              currentTarget: { name: 'nombreListaEnvio', value: listaAsociada.nombreLista }
            })
          }
        }
      }
    } catch (error) {
      const mensaje = 'Ocurrió un error al cargar las listas.'
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  useEffect(() => {
    if (idUsuario) {
      traerInfo()
    }
  }, [idUsuario, actualizar, vieneDeAdministrar, idPublicacion])

  const onHandleCheckbox = (e, pkId) => {
    let pkName
    let arrSeleccion = values[e.target.name] || []
    let arrCompleto

    switch (e.target.name) {
      case 'mediosPagos':
        arrCompleto = mediosPagos
        pkName = 'idMedioPago'
        break
      case 'mediosEnvios':
        arrCompleto = mediosEnvios
        pkName = 'idMedioEnvio'
        break
      default:
        arrCompleto = garantias
        pkName = 'idGarantia'
    }

    const itemSeleccionado = arrCompleto.find((m) => m[pkName] === pkId)
    if (e.target.checked) {
      arrSeleccion = [...arrSeleccion, arrCompleto.filter((m) => m[pkName] === pkId)[0]]
    } else {
      arrSeleccion = arrSeleccion.filter((g) => g[pkName] !== pkId)
    }
    onChange({
      currentTarget: {
        name: e.target.name,
        value: arrSeleccion
      }
    })

    if (e.target.name === 'mediosEnvios' && !e.target.checked) {
      if (itemSeleccionado?.idMedioEnvio === ENVIO_GRATIS) {
        onChange({
          currentTarget: { name: 'descZona', value: '' }
        })
        onChange({
          currentTarget: { name: 'cantDiasEnvioGratis', value: null }
        })
      }

      if (itemSeleccionado?.idMedioEnvio === RETIRA_COMPRADOR) {
        onChange({
          currentTarget: {
            name: 'cantDiasRetiroComprador',
            value: null
          }
        })
        onChange({
          currentTarget: { name: 'descZonaRetiro', value: '' }
        })
      }
    }
  }

  const handleChange = (e) => {
    onAddGarantia(e.target.name, parseInt(e.target.value))
  }

  const handleChangeModal = (e) => {
    const { name, value, filaIndex = '' } = e.target
    const campo = filaIndex !== '' ? `${name}_${filaIndex}` : name
    const nuevasValidaciones = { ...errores }

    if (filaIndex === '') {
      dispatch(setModal({ values: { ...modalValues, [name]: value } }))
    } else {
      const copia = [...modalValues.grilla]
      copia[filaIndex] = { ...copia[filaIndex], [name]: value }
      dispatch(setModal({ values: { grilla: copia } }))
    }

    if (errores[campo]) {
      delete nuevasValidaciones[campo]
    }
    dispatch(setModal({ errores: nuevasValidaciones }))
  }

  const handleSeleccionListaEnvio = (name, value) => {
    setMedioEnvio((prevState) => ({
      ...prevState,
      idLista: String(value)
    }))
    const seleccionado = enviosUsuario.find((e) => String(e.idLista) === String(value))
    const nombreLista = seleccionado ? seleccionado.nombreLista : ''

    if (onChange) {
      onChange({ currentTarget: { name: 'idListaEnvio', value: String(value) } })
      onChange({ currentTarget: { name: 'nombreListaEnvio', value: nombreLista } })
    }
  }

  const agregarFila = () => {
    const nuevaFila = {
      zona: '',
      cantidadDesde: 0,
      cantidadHasta: 0,
      costo: 0,
      moneda: '',
      tEstimado: 0
    }

    dispatch(
      setModal({
        values: {
          grilla: [...(modalValues.grilla || []), nuevaFila]
        }
      })
    )
  }

  const handleConfirmarAgregado = async () => {
    const nuevaLista = {
      nombreLista: modalValues.nombreLista,
      vigenciaDesde: modalValues.vigenciaDesde,
      vigenciaHasta: modalValues.vigenciaHasta,
      idVendedor: idUsuario,
      grilla:
        modalValues.grilla?.map((fila) => ({
          zona: fila.zona,
          cantidadDesde: fila.cantidadDesde,
          cantidadHasta: fila.cantidadHasta,
          costo: fila.costo,
          moneda: fila.moneda,
          tEstimado: fila.tEstimado
        })) || []
    }

    try {
      await listaEnviosService.insertarLista(nuevaLista)

      const { data: listasActualizadas } =
        await listaEnviosService.buscarListaPorVendedor(idUsuario)

      setEnviosUsuario(listasActualizadas)
      setActualizar((prev) => !prev)

      const listaCreada = listasActualizadas.find(
        (l) => l.nombreLista.trim().toLowerCase() === nuevaLista.nombreLista.trim().toLowerCase()
      )

      if (listaCreada) {
        setMedioEnvio({
          idLista: String(listaCreada.idLista),
          nombreLista: listaCreada.nombreLista
        })

        if (onChange) {
          onChange({
            currentTarget: { name: 'idListaEnvio', value: Number(listaCreada.idLista) }
          })
          onChange({
            currentTarget: { name: 'nombreListaEnvio', value: listaCreada.nombreLista }
          })
        }
      }

      dispatch(setModal({ show: false }))
      dispatch(resetModal())
      toast.success('Lista creada correctamente')
    } catch (error) {
      const mensaje = `Ocurrió un error al agregar la lista.`
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  const handleConfirmModal = async () => {
    const errores = {}
    const { nombreLista, vigenciaDesde, vigenciaHasta, grilla } = modalValues

    if (!nombreLista || nombreLista.trim() === '') {
      errores.nombreLista = 'El nombre es obligatorio.'
    }

    if (!vigenciaDesde) {
      errores.vigenciaDesde = 'Debe seleccionar una fecha de comienzo de vigencia.'
    } else {
      const fechaDesdeStr = vigenciaDesde
      const fechaActualStr = new Date().toISOString().split('T')[0]

      if (fechaDesdeStr < fechaActualStr) {
        errores.vigenciaDesde = 'La fecha de inicio debe ser igual o posterior a la fecha actual.'
      }
    }

    if (!vigenciaHasta) {
      errores.vigenciaHasta = 'La fecha de fin de vigencia es obligatoria.'
    } else if (vigenciaHasta <= vigenciaDesde) {
      errores.vigenciaHasta = 'La fecha de fin de vigencia debe ser mayor que la de comienzo.'
    }

    grilla.forEach((fila, i) => {
      const { cantidadDesde, cantidadHasta, costo, moneda, tEstimado, zona } = fila

      if (!zona || zona.trim() === '') {
        errores[`zona_${i}`] = 'La zona es obligatoria.'
      }

      if (cantidadDesde < 0 || !cantidadDesde) {
        errores[`cantidadDesde_${i}`] = 'Debe ingresar una cantidad mínima.'
      }

      if (i > 0) {
        const zonaActual = fila.zona?.trim().toUpperCase()
        const desdeActual = Number(cantidadDesde)

        const filasMismaZona = grilla
          .filter((f, idx) => idx < i && f.zona?.trim().toUpperCase() === zonaActual)
          .sort((a, b) => Number(a.cantidadDesde) - Number(b.cantidadDesde))

        if (filasMismaZona.length > 0) {
          const filaAnterior = filasMismaZona[filasMismaZona.length - 1]
          const hastaAnterior = Number(filaAnterior.cantidadHasta)

          if (desdeActual !== hastaAnterior + 1) {
            errores[`cantidadDesde_${i}`] = `En la zona "${
              fila.zona
            }", la cantidad desde debe ser ${
              hastaAnterior + 1
            } para evitar brechas o superposiciones.`
          }
        }
      }

      const desde = Number(cantidadDesde)
      const hasta = Number(cantidadHasta)

      if (!hasta || hasta <= desde) {
        errores[`cantidadHasta_${i}`] = 'La cantidad máxima debe ser mayor que la mínima.'
      }

      if (costo <= 0 || !costo) {
        errores[`costo_${i}`] = 'Debe ingresar el valor del costo.'
      }

      if (!moneda) {
        errores[`moneda_${i}`] = 'Debe seleccionar una moneda.'
      }

      if (tEstimado <= 0 || !tEstimado) {
        errores[`tEstimado_${i}`] = 'Debe ingresar el tiempo estimado.'
      }
    })

    if (
      enviosUsuario.some(
        (l) => l.nombreLista.trim().toLowerCase() === nombreLista.trim().toLowerCase()
      )
    ) {
      errores.nombreLista = 'Ya existe una lista con este nombre.'
    }

    dispatch(setModal({ errores, type: 'form' }))

    if (Object.keys(errores).length > 0) {
      return false
    }

    try {
      await handleConfirmarAgregado()
      dispatch(resetModal())

      return true
    } catch (error) {
      const mensaje = 'Ocurrió un error al agregar la lista.'
      toastErrorLog(error, mensaje, 'ventasEspeciales')
      return false
    }
  }

  const eliminarFila = (index) => {
    dispatch(eliminarFilaGrid(index))
  }

  const copiarCampo = (name, value) => {
    dispatch(copiarCampoGrid({ name, value }))
  }

  const filtrarListasPorVigencia = (listas) => {
    const fechaActual = new Date()
    return listas.reduce((acc, lista) => {
      const vigenteDesde = validarRangoFechas(new Date(lista.vigenciaDesde), fechaActual, 'day')
      const vigenteHasta = validarRangoFechas(fechaActual, new Date(lista.vigenciaHasta), 'day')
      if (vigenteDesde && vigenteHasta) {
        acc.push({
          key: lista.idLista,
          value: String(lista.idLista),
          label: lista.nombreLista
        })
      }
      return acc
    }, [])
  }

  const listasVigentesFiltradas = useMemo(
    () => filtrarListasPorVigencia(enviosUsuario),
    [enviosUsuario]
  )

  const envioCompradorSeleccionado = values.mediosEnvios?.some(
    (m) => m.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR
  )

  const idListaSeleccionada = String(values?.idListaEnvio ?? medioEnvio?.idLista ?? '')

  const esListaVigente = useMemo(() => {
    if (!idListaSeleccionada) {
      return false
    }

    return listasVigentesFiltradas.some((l) => String(l.value) === idListaSeleccionada)
  }, [idListaSeleccionada, listasVigentesFiltradas])

  const mostrarAvisoListaVencida =
    envioCompradorSeleccionado && idListaSeleccionada !== '' && !esListaVigente

  const valorSelectLista = envioCompradorSeleccionado
    ? esListaVigente
      ? idListaSeleccionada
      : ''
    : ''

  return (
    <>
      {!vieneDeAdministrar && (
        <p className={styles.descripcionCuadroPublicacion}>
          Incluya aquí las condiciones de pago, garantías y medios y costos de envío.
        </p>
      )}
      <div className={styles.ventasEspecialesContainer}>
        <div className={styles.ventasGrid}>
          <div className={styles.ventasColumna}>
            <div className={styles.seccion}>
              <div className={styles.tituloSeccion}>Condiciones de Pago</div>
              {mediosPagos.map((medioPago) => (
                <div key={medioPago.idMedioPago} className={styles.checkboxRow}>
                  <div className={styles.flexRow}>
                    <Checkbox
                      name="mediosPagos"
                      type="checkbox"
                      checked={
                        values.mediosPagos
                          ? values.mediosPagos.some((m) => m.idMedioPago === medioPago.idMedioPago)
                          : false
                      }
                      onChange={(e) => onHandleCheckbox(e, medioPago.idMedioPago)}
                    />
                    <i className={medioPago.icono} aria-hidden="true" />
                    <span className="mr-1">{medioPago.nombre}</span>
                    {medioPago.idMedioPago === MERCADO_PAGO && medioPago.descripcion && (
                      <small className="warningMessage">{`(${medioPago.descripcion})`}</small>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.seccion}>
              <div className={styles.tituloSeccion}>Garantía</div>
              {garantias.map((garantia) => (
                <div key={garantia.idGarantia} className={styles.checkboxRow}>
                  <div className={styles.flexRow}>
                    <Checkbox
                      type="checkbox"
                      name="garantias"
                      checked={
                        values.garantias
                          ? values.garantias.some((g) => g.idGarantia === garantia.idGarantia)
                          : false
                      }
                      onChange={(e) => onHandleCheckbox(e, garantia.idGarantia)}
                    />
                    <span>
                      <i
                        className={`${garantia.icono} ${styles.iconoGarantia}`}
                        aria-hidden="true"
                      />
                      {garantia.nombre}
                    </span>
                  </div>
                </div>
              ))}
              <div className={styles.checkboxRow}>
                <div className={styles.flexRow}>
                  <Checkbox
                    type="checkbox"
                    name="extensionGarantia"
                    value={values}
                    onChange={onChange}
                  />
                  <span>
                    <i className="fa fa-file-text-o fa-fw" aria-hidden="true" />
                    Extensión de Garantía
                  </span>
                </div>
              </div>
              {values.extensionGarantia && (
                <div className={styles.extGarantiaContainer}>
                  <div>Tiempo de Extensión</div>
                  <NumericFormat
                    name="garantiaPlazo"
                    onChange={(e) => handleChange(e)}
                    className={styles.inputText}
                    thousandSeparator={false}
                    decimalScale={0}
                    value={values.garantiaPlazo || ''}
                  />
                  Meses
                  <div>Monto por la extensión</div>
                  <NumericFormat
                    name="garantiaMonto"
                    onChange={(e) => handleChange(e)}
                    className={styles.inputText}
                    thousandSeparator={false}
                    decimalScale={2}
                    value={values.garantiaMonto || ''}
                    placeholder="$0.00"
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.ventasColumna}>
            <div className={styles.seccion}>
              <div className={styles.tituloSeccion}>Medios de Envío</div>
              {mediosEnvios.map((envio) => {
                const seleccionado = values.mediosEnvios?.some(
                  (m) => m.idMedioEnvio === envio.idMedioEnvio
                )
                const esEnvioGratis = envio.idMedioEnvio === ENVIO_GRATIS
                const esRetiraComprador = envio.idMedioEnvio === RETIRA_COMPRADOR
                const esEnvioCargoComprador = envio.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR
                const deshabilitarCheck = esEnvioCargoComprador && mostrarAvisoListaVencida
                return (
                  <div key={envio.idMedioEnvio} className={styles.checkboxRow}>
                    <div className={styles.flexRow}>
                      <Checkbox
                        type="checkbox"
                        name="mediosEnvios"
                        checked={seleccionado}
                        onChange={(e) => onHandleCheckbox(e, envio.idMedioEnvio)}
                        disabled={deshabilitarCheck}
                      />
                      <span>
                        <i className={`${envio.icono} ${styles.iconoMedio}`} aria-hidden="true" />
                        {envio.nombre}
                      </span>
                    </div>
                    {esEnvioCargoComprador && seleccionado && (
                      <div className={styles.agregarLista}>
                        <div className="col contenedor-select-medio-envio">
                          <div className="d-flex align-items-center">
                            <div
                              className={styles.agregarListaBtn}
                              onClick={abrirModalLista}
                              title="Agregar nueva lista de envio"
                            >
                              +
                            </div>
                            <GenericSelect
                              options={listasVigentesFiltradas}
                              className="select-medio-envio"
                              value={valorSelectLista}
                              onChange={(nuevoValor) => {
                                handleSeleccionListaEnvio('idLista', nuevoValor)
                                onChange(
                                  {
                                    currentTarget: {
                                      name: 'idListaEnvio',
                                      value: nuevoValor
                                    }
                                  },
                                  'num'
                                )
                              }}
                              errores={errores}
                              name="idListaEnvio"
                              placeholder="Seleccione una lista"
                              id="listaEnvio"
                            />
                          </div>
                          <div className={mostrarAvisoListaVencida ? 'mt-2' : ''}>
                            {mostrarAvisoListaVencida && (
                              <small className="warningMessage">
                                {`La lista "${values?.nombreListaEnvio ?? medioEnvio?.nombreLista ?? ''}" ya no se encuentra vigente. Por favor, actualice la misma en el menu Ventas-> Listas de envios.`}
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {esEnvioGratis && seleccionado && (
                      <div className="col-12">
                        <div className={styles.flexColumn}>
                          <div className={styles.flexRow}>
                            <textarea
                              className={styles.textarea}
                              placeholder="Zonas con envío gratis..."
                              value={values.descZona}
                              onChange={(e) => {
                                onChange({
                                  currentTarget: {
                                    name: 'descZona',
                                    value: e.target.value
                                  }
                                })
                              }}
                              rows={5}
                              maxLength={255}
                            />
                          </div>
                          <div className={styles.flexRow}>
                            <input
                              type="number"
                              className={styles.inputDias}
                              placeholder="Días aprox."
                              min={1}
                              value={values.cantDiasEnvioGratis ?? ''}
                              onChange={(e) => {
                                const value = e.target.value
                                onChange({
                                  currentTarget: {
                                    name: 'cantDiasEnvioGratis',
                                    value: value === '' ? null : Number(value)
                                  }
                                })
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {esRetiraComprador && seleccionado && (
                      <div className="col-12">
                        <div className={styles.flexColumn}>
                          <textarea
                            className={styles.textarea}
                            placeholder="Zonas con retiro por comprador..."
                            value={values.descZonaRetiro || ''}
                            onChange={(e) => {
                              onChange({
                                currentTarget: {
                                  name: 'descZonaRetiro',
                                  value: e.target.value
                                }
                              })
                            }}
                            rows={5}
                            maxLength={255}
                          />
                          <input
                            type="number"
                            className={styles.inputDias}
                            placeholder="Días aprox."
                            min={0}
                            value={values.cantDiasRetiroComprador ?? ''}
                            onChange={(e) => {
                              const value = e.target.value
                              onChange({
                                currentTarget: {
                                  name: 'cantDiasRetiroComprador',
                                  value: value === '' ? null : Number(value)
                                }
                              })
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <GenericModal
              modalId="crear-lista-envio"
              onConfirm={handleConfirmModal}
              onChange={handleChangeModal}
              onAddItem={agregarFila}
              onRemoveItem={eliminarFila}
              onCopyField={copiarCampo}
            />
          </div>
        </div>
      </div>
    </>
  )
}

VentasEspeciales.propTypes = {
  values: PropTypes.shape({
    mediosPagos: PropTypes.arrayOf(PropTypes.object),
    garantias: PropTypes.arrayOf(PropTypes.object),
    mediosEnvios: PropTypes.arrayOf(PropTypes.object),
    extensionGarantia: PropTypes.bool,
    idListaEnvio: PropTypes.number,
    garantiaPlazo: PropTypes.number,
    garantiaMonto: PropTypes.number,
    nombreListaEnvio: PropTypes.string,
    descZona: PropTypes.string,
    descZonaRetiro: PropTypes.string,
    cantDiasEnvioGratis: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cantDiasRetiroComprador: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onAddGarantia: PropTypes.func.isRequired,
  vieneDeAdministrar: PropTypes.bool,
  idPublicacion: PropTypes.number
}

export default VentasEspeciales
